const HOUR = 3600;
const DAY = 86400;
const WEEK = 604800;

/**
 * @typedef {Object} WorkflowBucket
 * @property {Date} bucketStart - Start time of the bucket
 * @property {Array<Object>} rows - Workflow runs in this bucket
 * @property {number} successRuns - Count of successful runs (no errors or warnings)
 * @property {number} warningRuns - Count of runs with warnings
 * @property {number} errorRuns - Count of failed/errored runs
 * @property {number} totalRuns - Total number of runs in the bucket
 * @property {number} totalErrors - Total number of errors across all runs in the bucket
 * @property {number} totalSuccess - Total number of successful changes across all runs in the bucket
 */

/**
 * @typedef {Object} BucketedWorkflowRuns
 * @property {number} bucketIntervalSeconds - The time interval between buckets in seconds
 * @property {number} targetColumns - The target number of columns for visualization
 * @property {Date} minDate - The minimum date of the entire range
 * @property {Date} maxDate - The maximum date of the entire range
 * @property {Array<WorkflowBucket>} buckets - Array of bucket objects sorted by bucketStart
 */

/**
 * Determines the target number of columns based on the total number of logs.
 * @param {number} totalLogs - The total number of logs to be displayed
 * @returns {number} The target number of columns for the visualization
 */
const getTargetColumns = (totalLogs) => {
  if (totalLogs <= 50) return 24;
  if (totalLogs <= 200) return 30;
  if (totalLogs <= 500) return 36;
  if (totalLogs <= 1000) return 42;
  return 48;
};

const generateBuckets = (bucketMinDate, maxDate, bucketIntervalSeconds) => {
  const buckets = new Map();

  let cursor = new Date(bucketMinDate);

  while (cursor <= maxDate) {
    buckets.set(cursor.toISOString(), {
      bucketStart: new Date(cursor),
      successRuns: 0,
      warningRuns: 0,
      errorRuns: 0,
      totalRuns: 0,
      totalErrors: 0,
      totalSuccess: 0
    });

    cursor = new Date(cursor.getTime() + bucketIntervalSeconds * 1000);
  }

  return buckets;
};

const alignDateToInterval = (date, bucketIntervalSeconds) => {
  const seconds = Math.floor(date.getTime() / 1000);
  const alignedSeconds = Math.floor(seconds / bucketIntervalSeconds) * bucketIntervalSeconds;

  return new Date(alignedSeconds * 1000);
};

const getBucketMinDate = (minDate, maxDate, bucketIntervalSeconds) => {
  const desiredStart = new Date(Math.min(minDate.getTime(), maxDate.getTime() - DAY * 1000));

  return alignDateToInterval(desiredStart, bucketIntervalSeconds);
};

/**
 * Calculates the bucket interval in seconds based on the date range and target columns.
 * @param {Date} minDate - The minimum date in the range
 * @param {Date} maxDate - The maximum date in the range
 * @param {number} targetColumns - The desired number of columns
 * @returns {number} The bucket interval in seconds
 */
const getBucketIntervalSeconds = (minDate, maxDate, targetColumns) => {
  const rangeSeconds = (maxDate.getTime() - minDate.getTime()) / 1000;
  const ideal = rangeSeconds / targetColumns;

  if (ideal < HOUR) {
    return HOUR; // minimum 1 hour
  }

  if (ideal < DAY) {
    return Math.floor(ideal / HOUR) * HOUR;
  }

  if (ideal < WEEK) {
    return Math.floor(ideal / DAY) * DAY;
  }

  return Math.floor(ideal / WEEK) * WEEK;
};

/**
 * Determines the start time of the bucket for a given date.
 * @param {Date} date - The date to find the bucket for
 * @param {Date} minDate - The minimum date reference point
 * @param {number} bucketIntervalSeconds - The interval of each bucket in seconds
 * @returns {Date} The start date of the bucket
 */
const getBucketStart = (date, minDate, bucketIntervalSeconds) => {
  const deltaSeconds = (date.getTime() - minDate.getTime()) / 1000;

  const bucketIndex = Math.floor(deltaSeconds / bucketIntervalSeconds);

  return new Date(minDate.getTime() + bucketIndex * bucketIntervalSeconds * 1000);
};

/**
 * Groups workflow runs into time-based buckets and aggregates their statistics.
 * @param {Array<Object>} rows - Array of workflow run records
 * @param {Object} [options={}] - Optional configuration
 * @param {Date|string} [options.from] - Custom minimum date for bucketing
 * @param {Date|string} [options.to] - Custom maximum date for bucketing
 * @returns {BucketedWorkflowRuns} Bucketed data object with aggregated workflow run statistics
 */
const bucketWorkflowRuns = (rows, options = {}) => {
  if (!rows.length) return [];

  const dates = rows.map((r) => new Date(r.startedOn));

  const minDate = options.from ? new Date(options.from) : new Date(Math.min(...dates));

  const maxDate = options.to ? new Date(options.to) : new Date(Math.max(...dates));

  const targetColumns = getTargetColumns(rows.length);

  // bucket size FIRST
  const bucketIntervalSeconds = getBucketIntervalSeconds(minDate, maxDate, targetColumns);

  // THEN compute a correctly aligned bucket start
  const bucketMinDate = getBucketMinDate(minDate, maxDate, bucketIntervalSeconds);

  const buckets = generateBuckets(bucketMinDate, maxDate, bucketIntervalSeconds);

  rows.forEach((row) => {
    const startedOn = new Date(row.startedOn);
    const bucketStart = getBucketStart(startedOn, bucketMinDate, bucketIntervalSeconds);

    const bucket = buckets.get(bucketStart.toISOString());
    if (!bucket) return;

    bucket.totalRuns++;

    const errorCount = Number(row.errorCount || 0);
    const warningCount = Number(row.warningCount || 0);
    const changeCount = Number(row.changeCount || 0);

    bucket.totalErrors += errorCount;
    bucket.totalSuccess += changeCount;

    if (row.statusId === 2 && errorCount === 0 && warningCount === 0) {
      bucket.successRuns++;
    } else if (row.statusId === 2 && errorCount === 0 && warningCount > 0) {
      bucket.warningRuns++;
    } else if ([3, 4, 5].includes(row.statusId)) {
      bucket.errorRuns++;
    }
  });

  return {
    bucketIntervalSeconds,
    targetColumns,
    bucketMinDate,
    maxDate,
    buckets: Array.from(buckets.values())
  };
};

export default bucketWorkflowRuns;
